// `CompileService.renderPrint`, `previewPDF` and the PDF path of `render` (SuiteCompile/CompileService.swift), over the
// Typst engine in a worker (@writing-suite/typst's `Typst`, or anything with its shape). A print interior is typeset
// up to five times: blank scene breaks that land at a page edge get the preset's ornament, and a page that runs short
// of its facing page gets a paragraph tracked ≤ 0.03 em looser so it gains a line. Typst's memo cache is emptied
// between passes (`evict(0)`): in wasm the heap only grows, and a 150,000-word book's passes would pass 2 GB.
import type { LastPrint } from '../publish.ts';
import type { PreflightFinding, CompiledOutput } from '../preflight.ts';
import type { CompiledManuscript, CompilePreset } from '../types.ts';
import { PrintLayout, type TypstLayout } from './layout.ts';
import { convertToPDFX } from './pdfx.ts';
import { printPreflight } from './preflight.ts';
import { screenplayFontFamilies, screenplayTypst } from '../screenplay/typst.ts';
import { stageUKTypst } from '../screenplay/stageTypst.ts';
import { writeFDX, writeFountain } from '../screenplay/writers.ts';
import { typstDocument, typstFontFamilies, typstSource, type BlockKey, type TypstDocument } from './writer.ts';

/** What this module needs of the engine (`Typst` in @writing-suite/typst has this shape). */
export interface TypstEngine {
  /** Loads the faces a Typst `font:` list names. */
  setFonts(families: readonly string[]): Promise<unknown>;
  compilePDF(source: string, options?: { printReady?: boolean; runs?: boolean }): Promise<{ pdf: Uint8Array; pages: number }>;
  compileLayout(source: string, options?: { printReady?: boolean; runs?: boolean }): Promise<{ pdf: Uint8Array; pages: number; layout: TypstLayout }>;
  /** Empties Typst's memo cache (0) or keeps the last `maxAge` compiles. */
  evict(maxAge?: number): Promise<void>;
}

export interface PrintPass {
  pass: number;
  document: TypstDocument;
  pages: number;
  ms: number;
}

export interface RenderPrintOptions {
  maxPasses?: number;
  maxTracking?: number;
  /** false skips the PDF/X step (the Publish preview). */
  pdfx?: boolean;
  /** The PDF/X dates. */
  date?: Date;
  /** `{year}` in title lines. */
  year?: number;
  /** Called after each pass is typeset (progress; fixtures). */
  onPass?: (pass: PrintPass) => void;
}

export interface RenderedPrint {
  pdf: Uint8Array;
  layout: PrintLayout;
  passes: number;
}

/** `CompileService.renderPrint`. */
export async function renderPrint(m: CompiledManuscript, typst: TypstEngine, o: RenderPrintOptions = {}): Promise<RenderedPrint> {
  const print = m.preset.render.print;
  if (!print) throw new Error('renderPrint needs a print preset');
  const maxPasses = o.maxPasses ?? 5, maxTracking = o.maxTracking ?? 0.03;
  const makesPDFX = (o.pdfx ?? true) && print.pdfx !== undefined;
  await typst.setFonts(typstFontFamilies(m));
  const finish = (pdf: Uint8Array, layout: PrintLayout, passes: number): RenderedPrint =>
    ({ pdf: makesPDFX ? convertToPDFX(pdf, m.title, print.bleed, o.date) : pdf, layout, passes });
  const ornament = print.pageEdgeOrnament.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '') !== '';
  const ornamented = new Set<number>();
  const loosened = new Map<BlockKey, number>();
  let pass = 0;
  try {
    for (;;) {
      pass += 1;
      if (pass > 1) await typst.evict(0);
      const document = typstDocument(m, { ornamentBreaks: ornamented, loosen: loosened, year: o.year });
      const started = Date.now();
      const { pdf, layout: typstLayout } = await typst.compileLayout(document.source, { printReady: makesPDFX });
      const layout = new PrintLayout(document, typstLayout);
      layout.ornamentedBreaks = ornamented.size;
      layout.loosenedParagraphs = loosened.size;
      o.onPass?.({ pass, document, pages: layout.pageCount, ms: Date.now() - started });
      if (pass >= maxPasses) return finish(pdf, layout, pass);
      let changed = false;
      const invisible = layout.invisibleBreaks();
      if (ornament && invisible.some((b) => !ornamented.has(b))) {
        for (const b of invisible) ornamented.add(b);
        changed = true;
      }
      if (print.balanceSpreads) {
        const metrics = layout.metrics();
        const used = new Set<BlockKey>();
        for (const spread of layout.imbalancedSpreads(metrics)) {
          let needed = spread.lines;
          for (const candidate of layout.loosenCandidates(spread.short, metrics, maxTracking)) {
            if (needed <= 0) continue;
            const total = (loosened.get(candidate.key) ?? 0) + candidate.tracking;
            if (!(total <= maxTracking) || used.has(candidate.key)) continue;
            used.add(candidate.key);
            loosened.set(candidate.key, total);
            needed -= 1;
            changed = true;
          }
        }
      }
      if (!changed) return finish(pdf, layout, pass);
    }
  } finally {
    // A full build's passes each typeset the whole book; what they memoised is no use afterwards.
    if (maxPasses > 1) await typst.evict(0).catch(() => {});
  }
}

export interface CompiledPDFResult {
  pdf: Uint8Array;
  pages: number;
  /** Print preflight (print presets); empty for other PDFs. */
  findings: PreflightFinding[];
  /** What the app keeps for the Publish room and the cover (`PublishReadiness.LastPrint`); print presets only. */
  lastPrint: LastPrint | null;
  /** The print layout, for copyfit notes and "Show in Editor". */
  layout: PrintLayout | null;
}

export interface CompilePDFOptions extends RenderPrintOptions {
  /** Run print preflight (default true). */
  preflight?: boolean;
}

/** A PDF of any PDF preset: screenplays as scripts, print interiors through `renderPrint` with print preflight, the
 *  rest (Shunn, reading copy, synopsis) in one pass. `preset` replaces the manuscript's (the same one, usually). */
export async function compilePDF(manuscript: CompiledManuscript, preset: CompilePreset | null, typst: TypstEngine,
  o: CompilePDFOptions = {}): Promise<CompiledPDFResult> {
  const m = preset && preset !== manuscript.preset ? { ...manuscript, preset } : manuscript;
  if (m.preset.render.screenplay) {
    await typst.setFonts(screenplayFontFamilies(m));
    const { pdf, pages } = await typst.compilePDF(await scriptSource(m, typst));
    return { pdf, pages, findings: [], lastPrint: null, layout: null };
  }
  if (m.preset.render.print) {
    const { pdf, layout } = await renderPrint(m, typst, o);
    const findings = o.preflight === false ? [] : printPreflight({ data: pdf, manuscript: m, layout });
    const lastPrint: LastPrint = { pages: layout.pageCount, ornamentedBreaks: layout.ornamentedBreaks, loosenedParagraphs: layout.loosenedParagraphs,
      presetID: m.preset.id, size: m.preset.render.page.size };
    return { pdf, pages: layout.pageCount, findings, lastPrint, layout };
  }
  await typst.setFonts(typstFontFamilies(m));
  const { pdf, pages } = await typst.compilePDF(typstSource(m, { year: o.year }));
  return { pdf, pages, findings: [], lastPrint: null, layout: null };
}

/** `CompileService.previewPDF`: a proof of a (sample) manuscript whatever the preset's format: scripts as scripts,
 *  print interiors in one pass without PDF/X, Word or text presets as the same layout through Typst. */
export async function previewPDF(m: CompiledManuscript, typst: TypstEngine, o: { year?: number } = {}): Promise<Uint8Array> {
  if (m.preset.render.screenplay) {
    await typst.setFonts(screenplayFontFamilies(m));
    return (await typst.compilePDF(await scriptSource(m, typst))).pdf;
  }
  if (m.preset.render.print) return (await renderPrint(m, typst, { maxPasses: 1, pdfx: false, year: o.year })).pdf;
  await typst.setFonts(typstFontFamilies(m));
  return (await typst.compilePDF(typstSource(m, { year: o.year }))).pdf;
}

/** A script's Typst source: a UK play's sample cut where Typst's own pages end (`StageUKTypst.printed(m, engine:)`). */
async function scriptSource(m: CompiledManuscript, typst: TypstEngine): Promise<string> {
  if (m.preset.render.screenplay?.format === 'stage-uk' && m.preset.render.screenplay.sample) {
    return stageUKTypst(m, async (source) => (await typst.compilePDF(source)).pages);
  }
  return screenplayTypst(m);
}

/** The hooks `compile` takes: pass the result as `renderers` (PDF through Typst, keeping its layout; FDX; Fountain)
 *  and its `preflight` as `preflight.pdf` (print preflight of the PDF just built). */
export function typstRenderers(typst: TypstEngine, o: RenderPrintOptions = {}): {
  pdf: (m: CompiledManuscript) => Promise<Uint8Array>;
  fdx: (m: CompiledManuscript) => Uint8Array;
  fountain: (m: CompiledManuscript) => string;
  /** The pages Typst sets a source on: where a UK play's sample ends, for its Word version. */
  typstPages: (source: string) => Promise<number | null>;
  preflight: (output: CompiledOutput) => PreflightFinding[];
  /** The last build of a manuscript, for `LastPrint` and copyfit notes. */
  built: (m: CompiledManuscript) => CompiledPDFResult | undefined;
} {
  const results = new WeakMap<CompiledManuscript, CompiledPDFResult>();
  return {
    pdf: async (m) => {
      const result = await compilePDF(m, null, typst, { ...o, preflight: true });
      results.set(m, result);
      return result.pdf;
    },
    fdx: writeFDX,
    fountain: writeFountain,
    typstPages: async (source) => {
      await typst.setFonts(['Times New Roman', 'Libertinus Serif']);
      return (await typst.compilePDF(source)).pages;
    },
    preflight: (output) => results.get(output.manuscript)?.findings ?? [],
    built: (m) => results.get(m),
  };
}
